import { Order } from 'components/common/table';
import { Column } from 'components/common/table/table';
import { useQueryParams, useUpdateQueryParams } from 'hooks/use-query-params';

const cleanQueryParamAsNumber = (val: string, defaultVal?: number): number => {
  const parsed = parseInt(val, 10);
  if (isNaN(parsed)) {
    return defaultVal ?? 0;
  }
  return parsed;
};

const cleanQueryParamOrder = (val: string): Order | undefined => {
  if (val === 'asc') return 'asc';
  if (val === 'desc') return 'desc';
};

type NavTableConnectionOptions<Data> = {
  defaultOrderBy?: Extract<keyof Data, string>;
  defaultOrder?: 'asc' | 'desc';
};

export function useNavTableConnection<Data>(
  columns: readonly Column<Data>[],
  { defaultOrderBy, defaultOrder }: NavTableConnectionOptions<Data> = {},
) {
  const query = useQueryParams();
  const queryUpdater = useUpdateQueryParams();

  const queryParamPage = query.get('page') ?? '';
  const queryParamRowsPerPage = query.get('rowsPerPage') ?? '';
  const queryParamOrder = query.get('order') ?? defaultOrder ?? '';
  const queryParamOrderBy = query.get('orderBy') ?? defaultOrderBy ?? '';

  const cleanQueryParamOrderBy = (val: string): keyof Data | undefined => {
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].id === val) {
        return columns[i].id;
      }
    }
  };

  const page = cleanQueryParamAsNumber(queryParamPage);
  const rowsPerPage = cleanQueryParamAsNumber(queryParamRowsPerPage, 100);
  const order = cleanQueryParamOrder(queryParamOrder);
  const orderBy = cleanQueryParamOrderBy(queryParamOrderBy);

  const onRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    query.set('orderBy', String(property));
    query.set('order', isAsc ? 'desc' : 'asc');
    queryUpdater(query, { replace: true });
  };

  // if we need to find the default orderBy then the order will be set automatically
  if (!orderBy) {
    const defaultOrderBy = columns.find((column) => column.isDefaultSortColumn);
    if (defaultOrderBy) {
      onRequestSort(defaultOrderBy.id);
    }
  } else if (!order) {
    // if we have an orderBy but no order, we force an order into the url and table
    onRequestSort(orderBy);
  }

  const setPage = (page: number) => {
    query.set('page', `${page}`);
    queryUpdater(query, { replace: true });
  };

  const setRowsPerPage = (rows: number) => {
    query.set('rowsPerPage', `${rows}`);
    queryUpdater(query, { replace: true });
  };

  return {
    page,
    rowsPerPage,
    order,
    orderBy,
    onRequestSort,
    setPage,
    setRowsPerPage,
    columns,
  };
}
